import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  saveAdminPlan,
  deleteAdminPlan,
  type PlanPayload,
} from "@/services/adminService";
import type { AdminPlan, AdminPlansBundle } from "@/types/admin";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import PlanLimitPreview from "./PlanLimitPreview";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const ICON_CHOICES = [
  "ri-store-3-line",
  "ri-rocket-2-line",
  "ri-vip-crown-2-line",
  "ri-building-4-line",
  "ri-medal-line",
];

interface PlanForm {
  name: string;
  slug: string;
  tagline: string;
  priceYearly: number;
  durationDays: number;
  icon: string;
  isPopular: boolean;
  isActive: boolean;
  displayOrder: number;
  highlights: string;
}

const EMPTY_FORM: PlanForm = {
  name: "",
  slug: "",
  tagline: "",
  priceYearly: 0,
  durationDays: 365,
  icon: "ri-store-3-line",
  isPopular: false,
  isActive: true,
  displayOrder: 1,
  highlights: "",
};

interface PlansTabProps {
  token: string | null;
  data: AdminPlansBundle;
  setData: Dispatch<SetStateAction<AdminPlansBundle>>;
  loading: boolean;
}

/** Admin tab — create / edit / remove the plans themselves. */
export default function PlansTab({ token, data, setData, loading }: PlansTabProps) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<PlanForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminPlan | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [...data.plans].sort((a, b) => a.displayOrder - b.displayOrder);
    if (!q) return rows;
    return rows.filter((p) => `${p.name} ${p.slug} ${p.tagline}`.toLowerCase().includes(q));
  }, [data.plans, search]);

  function openCreate() {
    const nextOrder = data.plans.reduce((max, p) => Math.max(max, p.displayOrder), 0) + 1;
    setForm({ ...EMPTY_FORM, displayOrder: nextOrder });
    setEditId(null);
  }

  function openEdit(plan: AdminPlan) {
    setForm({
      name: plan.name,
      slug: plan.slug,
      tagline: plan.tagline,
      priceYearly: plan.priceYearly,
      durationDays: plan.durationDays,
      icon: plan.icon,
      isPopular: plan.isPopular,
      isActive: plan.isActive,
      displayOrder: plan.displayOrder,
      highlights: plan.highlights.join("\n"),
    });
    setEditId(plan.id);
  }

  function setField<K extends keyof PlanForm>(key: K, value: PlanForm[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!form?.name.trim()) return;
    const highlights = form.highlights.split("\n").map((h) => h.trim()).filter(Boolean);
    const payload: PlanPayload = {
      name: form.name.trim(),
      slug: form.slug.trim() || form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      tagline: form.tagline,
      priceYearly: form.priceYearly,
      durationDays: form.durationDays,
      icon: form.icon,
      isPopular: form.isPopular,
      isActive: form.isActive,
      displayOrder: form.displayOrder,
      highlights,
    };

    setBusy(true);
    try {
      await saveAdminPlan(token, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => ({
          ...prev,
          plans: prev.plans.map((p) => (p.id === editId ? { ...p, ...payload } as AdminPlan : p)),
        }));
      } else {
        setData((prev) => ({
          ...prev,
          plans: [
            ...prev.plans,
            { id: Date.now(), ...(payload as Omit<AdminPlan, "id">) } as AdminPlan,
          ],
        }));
      }
      setForm(null);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(plan: AdminPlan) {
    const next = !plan.isActive;
    setData((prev) => ({
      ...prev,
      plans: prev.plans.map((p) => (p.id === plan.id ? { ...p, isActive: next } : p)),
    }));
    await saveAdminPlan(token, { isActive: next }, plan.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteAdminPlan(token, deleteTarget.id);
      setData((prev) => ({ ...prev, plans: prev.plans.filter((p) => p.id !== deleteTarget.id) }));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search plans…" />
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New plan
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${data.plans.length} plans`}
        </span>
      </div>

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-price-tag-3-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No plans yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Plan</th>
                  <th className={adminTh}>Price / year</th>
                  <th className={adminTh}>Validity</th>
                  <th className={adminTh}>Highlights</th>
                  <th className={adminTh}>Popular</th>
                  <th className={adminTh}>Active</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((plan) => (
                  <tr key={plan.id} className="hover:bg-background-100/40">
                    <td className={`${adminTd} tabular-nums`}>{plan.displayOrder}</td>
                    <td className={adminTd}>
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                          <i className={`${plan.icon} text-base`} />
                        </span>
                        <div>
                          <p className="font-semibold text-foreground-900">{plan.name}</p>
                          <p className="text-[11px] text-foreground-400">/{plan.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className={`${adminTd} tabular-nums`}>
                      {plan.priceYearly === 0 ? "Free" : `₹${plan.priceYearly.toLocaleString("en-IN")}`}
                    </td>
                    <td className={adminTd}>
                      {plan.priceYearly === 0 ? "For ever" : `${plan.durationDays} days`}
                    </td>
                    <td className={`${adminTd} tabular-nums`}>{plan.highlights.length}</td>
                    <td className={adminTd}>
                      {plan.isPopular ? (
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-bold text-accent-800">
                          Popular
                        </span>
                      ) : (
                        <span className="text-foreground-300">—</span>
                      )}
                    </td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => toggleActive(plan)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          plan.isActive
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {plan.isActive ? "Live" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(plan)} className={adminIconBtn} title="Edit plan">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(plan)}
                          className={adminIconBtn}
                          title="Delete plan"
                        >
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (
        <AdminModal
          title={editId ? "Edit plan" : "New plan"}
          subtitle="Shown as a card on the public listing-plans page"
          wide
          onClose={() => setForm(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!form.name.trim() || busy}
                onClick={submit}
                className={adminBtnPrimary}
              >
                {editId ? "Save changes" : "Create plan"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="pl-name">Plan name</label>
                <input
                  id="pl-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. Business"
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="pl-slug">Slug</label>
                <input
                  id="pl-slug"
                  type="text"
                  value={form.slug}
                  onChange={(e) => setField("slug", e.target.value)}
                  placeholder="business"
                  className={adminInput}
                />
              </div>
            </div>

            <div>
              <label className={adminLabel} htmlFor="pl-tagline">Tagline</label>
              <input
                id="pl-tagline"
                type="text"
                value={form.tagline}
                onChange={(e) => setField("tagline", e.target.value)}
                placeholder="One line describing who the plan is for"
                className={adminInput}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={adminLabel} htmlFor="pl-price">Price / year (₹)</label>
                <input
                  id="pl-price"
                  type="number"
                  min={0}
                  value={form.priceYearly}
                  onChange={(e) => setField("priceYearly", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="pl-days">Validity (days)</label>
                <input
                  id="pl-days"
                  type="number"
                  min={0}
                  value={form.durationDays}
                  onChange={(e) => setField("durationDays", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
              <div>
                <label className={adminLabel} htmlFor="pl-order">Display order</label>
                <input
                  id="pl-order"
                  type="number"
                  min={0}
                  value={form.displayOrder}
                  onChange={(e) => setField("displayOrder", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
            </div>

            <div>
              <label className={adminLabel} htmlFor="pl-icon">Icon</label>
              <input
                id="pl-icon"
                type="text"
                value={form.icon}
                onChange={(e) => setField("icon", e.target.value)}
                className={adminInput}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {ICON_CHOICES.map((ic) => (
                  <button
                    key={ic}
                    type="button"
                    onClick={() => setField("icon", ic)}
                    className={`flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border transition-colors ${
                      form.icon === ic
                        ? "border-primary-400 bg-primary-50 text-primary-700"
                        : "border-background-300 text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    <i className={`${ic} text-base`} />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={adminLabel} htmlFor="pl-highlights">
                Highlights (one per line)
              </label>
              <textarea
                id="pl-highlights"
                value={form.highlights}
                onChange={(e) => setField("highlights", e.target.value)}
                rows={6}
                placeholder={"1 verified listing\nList in 5 Categories\nLead dashboard & enquiry alerts"}
                className={`${adminInput} h-auto resize-y py-2.5 leading-relaxed`}
              />
            </div>

            <PlanLimitPreview
              name={form.name}
              slug={form.slug}
              tagline={form.tagline}
              highlightsText={form.highlights}
            />

            <div className="flex flex-wrap gap-5">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(e) => setField("isPopular", e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-primary-500"
                />
                Mark as most popular
              </label>
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setField("isActive", e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-primary-500"
                />
                Visible on the pricing page
              </label>
            </div>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Delete plan"
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button type="button" disabled={busy} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete plan
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete <strong>{deleteTarget.name}</strong>? If the plan is still used by live listings it will be
            deactivated instead, so those listings are not affected.
          </p>
        </AdminModal>
      )}
    </div>
  );
}